import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  HStack,
  Icon,
  Image,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { PhoneNumberInput } from "components/PhoneNumberInput";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useGetGeoLocation } from "hooks/useGetGeoLocation";
import { useCallback, useContext, useMemo, useState } from "react";
import { MdMyLocation } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageInstitutionPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch } = useContext(Store);
  const [city, setCity] = useState("");
  const { location, getLocation } = useGetGeoLocation();
  const {
    getInstitution,
    addInstitution,
    updateInstitution,
    getCities,
    getDealers,
    countries,
    isCountryLoading,
  } = useApi();
  const {
    data: institution,
    isLoading,
    mutate,
  } = useCache("institution", () => getInstitution(+id || 0));
  const { data: dealers } = useCache("dealers", () => getDealers());

  const [profilePictureSrc, setProfilePictureSrc] = useState(null);


  const initialValues = useMemo(() => {
    return {
      name: institution?.name || "",
      email: institution?.email || "",
      firstName: institution?.first_name || "",
      lastName: institution?.last_name || "",
      country: institution?.country_id || "",
      phoneNumber: institution?.phone_number || "",
      profilePicture: institution?.profile_picture || "",
      paymentApiPk: institution?.payment_api_pk || "",
      paymentApiSk: institution?.payment_api_sk || "",
      emailConfirm: institution?.email || "",
      dealer: institution?.dealer_id || "",
      city: institution?.city_id || "",
      cityCountry: institution?.country_id || "",
      address: institution?.address || "",
      lat: institution?.lat || "",
      long: institution?.long || "",
      submit: null,
    };
  }, [institution]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Nom d'institution requis"),
    firstName: Yup.string().trim().required("Prenom requis"),
    lastName: Yup.string().trim().required("Nom requis"),
    paymentApiPk: Yup.string().trim().required("Clé publique API requis"),
    paymentApiSk: Yup.string().trim().required("Clé secrète API requis"),
    email: Yup.string().email().trim().required("email requis"),
    emailConfirm: Yup.string()
      .oneOf(
        [Yup.ref("email"), ""],
        "La email et la email de confirmation doivent être les mêmes"
      )
      .required("Confirmer l'email"),
    long: Yup.string().trim(),
    lat: Yup.string().trim(),
    phoneNumber: Yup.string()
      .trim()
      .required("le numéro de téléphone est requis"),
    // city retiré de la validation - n'est plus requis
    // country retiré - on utilise cityCountry à la place
    cityCountry: Yup.string().trim().required("pays requis"),
    address: Yup.string().trim().required("l'adresse est requise"),
    dealer: Yup.number().required("un revendeur est requis"),
    profilePicture: Yup.mixed(),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let formData: any = new FormData();
        if (profilePictureSrc) {
          formData.append(
            "profile_picture",
            values.profilePicture,
            values.profilePicture.name
          );
        }
        formData.append("name", values.name);
        formData.append("first_name", values.firstName);
        formData.append("last_name", values.lastName);
        formData.append("phone_number", values.phoneNumber);
        formData.append("country_id", values.cityCountry);
        formData.append("email", values.email);
        formData.append("payment_api_pk", values.paymentApiPk);
        formData.append("payment_api_sk", values.paymentApiSk);
        formData.append("email", values.email);
        formData.append("dealer_id", values.dealer);
        formData.append("city_id", values.city);
        formData.append("address", values.address);
        formData.append("lat", values.lat);
        formData.append("long", values.long);
        formData.append("is_active", true);
        if (id) {
          formData.append("id", id);
        }
        let response: any = null;
        if (id) {
          response = await updateInstitution(formData);
        } else {
          response = await addInstitution(formData);
        }
        if (!response.error_code && !response.errors) {
          if (response.id) {
            dispatch(
              showToast({
                toast: toast,
                message: id
                  ? "L'institution a été mise à jour avec succès."
                  : "L'institution a été créée avec succès.",
                status: toastTypes.success,
              })
            );
            if (id) {
              mutate(getInstitution(+id));
              navigate("/institutions");
            } else {
              navigate("/institutions");
            }
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const url = URL.createObjectURL(file);
    setProfilePictureSrc(url);
    setFieldValue("profilePicture", file);
  };
  const {
    data: cities,
    mutate: mutateCity,
    isLoading: isCityLoading,
  } = useCache("cities", () => getCities(values.cityCountry || "", 1, city));

  const handleGetLocation = useCallback(async () => {
    await getLocation();
    if (location) {
      setFieldValue("lat", location.latitude);
      setFieldValue("long", location.longitude);
    }
  }, [location, setFieldValue, getLocation]);
  // useEffect(() => {
  //   if (institution) mutateCity(getCities(values.cityCountry || "", 1, city));
  // }, [institution, mutateCity, getCities, city]);

  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minH={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier institution" : "Ajouter une institution"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="8px" flexDirection="column" alignItems="start">
                  <HStack>
                    <Text color="gray_description" mt="16px">
                      Logo de l’institution
                    </Text>
                    <span style={{ color: "red" }}>*</span>
                  </HStack>
                  <AppInput
                    type="file"
                    accept="image/*"
                    borderWidth="1px"
                    alignSelf="stretch"
                    borderRadius="8px"
                    name="profilePicture"
                    id="profilePicture"
                    onChange={(e: any) => handleFileChange(e)}
                    display="none"
                  />
                  <Flex
                    gap="16px"
                    alignSelf="stretch"
                    alignItems="center"
                    justifyContent={"flex-start"}
                    flexDirection={{ md: "row", base: "column" }}
                  >
                    <Image
                      src={
                        profilePictureSrc || institution?.profile_picture
                          ? profilePictureSrc || institution?.profile_picture
                          : "images/image_upload_preview.jpg"
                      }
                      alt="product Image"
                      h="100px"
                      alignSelf="center"
                      w={"100px"}
                      fit="contain"
                      borderRadius="12px"
                      onClick={() =>
                        document.getElementById("profilePicture").click()
                      }
                    />
                  </Flex>
                </Flex>
                <Flex gap="16px" flexDirection="column">
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Nom d'institution</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.name && (touched.name as boolean)}
                      >
                        <AppInput
                          required
                          placeholder={`Nom d'institution`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="name"
                          name="name"
                          autoComplete="name"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.name}
                          showErrorMessage={Boolean(
                            touched.name && errors.name
                          )}
                          errorMessage={errors.name}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                </Flex>
                <Flex gap="16px" flexDirection="column">
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Nom du représentant
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.lastName && (touched.lastName as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`Nom`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="lastName"
                          name="lastName"
                          autoComplete="lastName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.lastName}
                          showErrorMessage={Boolean(
                            touched.lastName && errors.lastName
                          )}
                          errorMessage={errors.lastName}
                        />
                      </FormControl>
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Prénoms du représentant
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.firstName && (touched.firstName as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`Prénoms`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="firstName"
                          name="firstName"
                          autoComplete="firstName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.firstName}
                          showErrorMessage={Boolean(
                            touched.firstName && errors.firstName
                          )}
                          errorMessage={errors.firstName}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Email</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.email && (touched.email as boolean)}
                      >
                        <AppInput
                          required
                          placeholder={`ronald.koffikra@gmail.com`}
                          type="email"
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="email"
                          name="email"
                          autoComplete="email"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.email}
                          showErrorMessage={Boolean(
                            touched.email && errors.email
                          )}
                          errorMessage={errors.email}
                        />
                      </FormControl>
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Confirmation de l'email
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.emailConfirm &&
                          (touched.emailConfirm as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`ronald.koffikra@gmail.com`}
                          type="email"
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="emailConfirm"
                          name="emailConfirm"
                          autoComplete="emailConfirm"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.emailConfirm}
                          showErrorMessage={Boolean(
                            touched.emailConfirm && errors.emailConfirm
                          )}
                          errorMessage={errors.emailConfirm}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Pays</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        width={"100%"}
                      >
                        <FormControl
                          isInvalid={
                            !!errors.cityCountry &&
                            (touched.cityCountry as boolean)
                          }
                        ></FormControl>
                        <AppSelect
                          id="cityCountry"
                          name="cityCountry"
                          onChange={(field: string, value: any) => {
                            setFieldValue(field, value);
                            mutateCity(getCities(value || ""));
                          }}
                          value={
                            values.cityCountry || initialValues.cityCountry
                          }
                          placeholder="Veuillez sélectionner un pays"
                          options={countries}
                          isMulti={false}
                          isLoading={isCountryLoading}
                          showErrorMessage={Boolean(
                            touched.cityCountry && errors.cityCountry
                          )}
                          errorMessage={errors.cityCountry}
                          onBlur={handleBlur}
                        />
                      </Flex>
                    </Flex>
                    {values.cityCountry && cities?.length > 0 && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                      >
                        <Text color="gray_description">Ville</Text>
                        <Flex
                          gap="4px"
                          flexDirection="column"
                          alignItems="start"
                          width={"100%"}
                        >
                          <FormControl
                            isInvalid={
                              !!errors.city && (touched.city as boolean)
                            }
                          ></FormControl>
                          <AppSelect
                            id="city"
                            name="city"
                            handleInputChange={(value) => {
                              setCity(value);
                              mutateCity(
                                getCities(values.cityCountry || "", 1, value)
                              );
                            }}
                            onChange={setFieldValue}
                            value={values.city || initialValues.city}
                            placeholder="Veuillez sélectionner une ville"
                            options={cities}
                            isMulti={false}
                            isLoading={isCityLoading}
                            showErrorMessage={Boolean(
                              touched.city && errors.city
                            )}
                            errorMessage={errors.city}
                            onBlur={handleBlur}
                          />
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Numéro de téléphone
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <PhoneNumberInput
                        name="phoneNumber"
                        onChange={setFieldValue}
                        value={values.phoneNumber}
                        countryDialCode={institution?.country_dial_code}
                        onBlur={handleBlur}
                        showErrorMessage={Boolean(
                          touched.phoneNumber && errors.phoneNumber
                        )}
                        errorMessage={errors.phoneNumber}
                      />
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Dealer</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        width={"100%"}
                      >
                        <AppSelect
                          id="dealer"
                          name="dealer"
                          onChange={(field: string, value: any) => {
                            setFieldValue(field, value);
                          }}
                          value={values.dealer || initialValues.dealer}
                          placeholder="Veuillez sélectionner un dealer"
                          options={dealers}
                          isMulti={false}
                          isLoading={isCityLoading}
                          showErrorMessage={Boolean(
                            touched.dealer && errors.dealer
                          )}
                          errorMessage={errors.dealer}
                          onBlur={handleBlur}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
                <Flex
                  flexDirection={{ md: "row", base: "column" }}
                  w={"full"}
                  gap={5}
                >
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <HStack>
                      <Text color="gray_description">Latitude</Text>
                      <Icon as={MdMyLocation as any} boxSize={4} onClick={handleGetLocation} />
                    </HStack>
                    <FormControl
                      isInvalid={!!errors.lat && (touched.lat as boolean)}
                    >
                      <AppInput
                        placeholder={`Latitude`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="lat"
                        name="lat"
                        autoComplete="lat"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.lat}
                        showErrorMessage={Boolean(touched.lat && errors.lat)}
                        errorMessage={errors.lat}
                      />
                    </FormControl>
                  </Flex>
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <HStack>
                      <Text color="gray_description">Longitude</Text>
                      <Icon as={MdMyLocation as any} boxSize={4} onClick={handleGetLocation} />
                    </HStack>
                    <FormControl
                      isInvalid={!!errors.long && (touched.long as boolean)}
                    >
                      <AppInput
                        placeholder={`Longitude`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="long"
                        name="long"
                        autoComplete="long"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.long}
                        showErrorMessage={Boolean(touched.long && errors.long)}
                        errorMessage={errors.long}
                      />
                    </FormControl>
                  </Flex>
                </Flex>
                <Flex gap="4px" flexDirection="column" alignItems="start">
                  <HStack>
                    <Text color="gray_description">Adresse</Text>
                    <span style={{ color: "red" }}>*</span>
                  </HStack>
                  <FormControl
                    isInvalid={!!errors.address && (touched.address as boolean)}
                  >
                    <Textarea
                      required
                      borderWidth="1px"
                      alignSelf="stretch"
                      borderRadius="8px"
                      id="address"
                      name="address"
                      autoComplete="address"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.address || initialValues.address}
                      // showErrorMessage={Boolean(touched.address && errors.address)}
                      // errorMessage={errors.address}
                    />
                    <FormErrorMessage style={{ color: "red" }}>
                      {typeof errors.address === "string" && errors.address}
                    </FormErrorMessage>
                  </FormControl>
                </Flex>
                <Flex
                  flexDirection={{ md: "row", base: "column" }}
                  w={"full"}
                  gap={5}
                >
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <HStack>
                      <Text color="gray_description">Clé publique API</Text>
                      <span style={{ color: "red" }}>*</span>
                    </HStack>
                    <FormControl
                      isInvalid={
                        !!errors.paymentApiPk &&
                        (touched.paymentApiPk as boolean)
                      }
                    >
                      <AppInput
                        required
                        type="password"
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="paymentApiPk"
                        name="paymentApiPk"
                        autoComplete="paymentApiPk"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.paymentApiPk}
                        showErrorMessage={Boolean(
                          touched.paymentApiPk && errors.paymentApiPk
                        )}
                        errorMessage={errors.paymentApiPk}
                      />
                    </FormControl>
                  </Flex>
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <HStack>
                      <Text color="gray_description">Clé secrète API</Text>
                      <span style={{ color: "red" }}>*</span>
                    </HStack>
                    <FormControl
                      isInvalid={
                        !!errors.paymentApiSk &&
                        (touched.paymentApiSk as boolean)
                      }
                    >
                      <AppInput
                        required
                        type="password"
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="paymentApiSk"
                        name="paymentApiSk"
                        autoComplete="paymentApiSk"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.paymentApiSk}
                        showErrorMessage={Boolean(
                          touched.paymentApiSk && errors.paymentApiSk
                        )}
                        errorMessage={errors.paymentApiSk}
                      />
                    </FormControl>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <Flex mt="24px" gap="12px">
              <Button
                size="4xl"
                color="gray_description"
                fontFamily="Arimo"
                fontWeight={700}
                minW="106px"
                borderRadius="8px"
                onClick={() => window.history.back()}
              >
                Annuler
              </Button>
              <Button
                size="4xl"
                colorScheme="red_origin"
                fontFamily="Arimo"
                fontWeight={700}
                minW="132px"
                borderRadius="8px"
                type="submit"
                isLoading={isSubmitting}
              >
                Enregistrer
              </Button>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
